/**
 * Kanban kartasi: ism, shtat va driverning mahalliy vaqti, CDL muddati, keyingi ish, egasi, bosqichda necha kun.
 * Tezlik uchun ikki qism: sudraladigan yengil o'ram (PipelineCard) va memo qilingan mazmun (CardContent).
 * Sudrash paytida dnd-kit context'i o'zgarsa, 200+ kartaning faqat o'rami qayta chiziladi, mazmuni emas.
 */
import { forwardRef, memo, useCallback, type HTMLAttributes } from "react";
import { useDraggable } from "@dnd-kit/core";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { cn, dayjs, formatWhen } from "@/lib/utils";
import type { DriverListItem } from "@/features/drivers/types";
import { ContextMenu, type MenuEntry } from "@/components/ui/context-menu";
import { PersonLink } from "@/components/PersonLink";
import { DriverLocalTime } from "@/features/drivers/DriverLocalTime";

export const CARD_PREFIX = "driver-";

interface NextAction {
  text: string;
  tone?: "danger";
}

/** Keyingi ish: eslatma bo'lsa u, bo'lmasa statusga qarab standart matn */
export function nextActionOf(d: DriverListItem, t: TFunction<"pipeline">, tc: TFunction): NextAction | null {
  if (d.next_reminder) {
    const label = tc(`reminder.${d.next_reminder.kind}`);
    if (d.next_reminder.overdue) return { text: t("card.overdue", { label }), tone: "danger" };
    return { text: t("card.next", { label, when: formatWhen(d.next_reminder.due_at) }) };
  }
  switch (d.status) {
    case "new":
      return { text: t("card.firstCall") };
    case "docs":
      return { text: t("card.collectDocs") };
    case "submitted":
      return { text: t("card.waitCarrier") };
    case "background_check":
      return { text: t("card.waitBg") };
    case "orientation":
      return { text: t("card.confirmOrientation") };
    default:
      return null;
  }
}

export interface PipelineCardProps {
  driver: DriverListItem;
  staleDays: number;
  onOpen?: (driver: DriverListItem) => void;
  /** O'ng tugma menyusi */
  menu?: (driver: DriverListItem) => MenuEntry[];
}

const CARD_BASE = "select-none rounded-2xl border border-border/60 bg-surface p-3 shadow-card";

/** Kartaning ko'rinishi (hook'siz): doskada ham, sudralayotgan nusxada ham */
const CardContent = memo(function CardContent({ driver, staleDays }: { driver: DriverListItem; staleDays: number }) {
  const { t } = useTranslation("pipeline");
  const { t: tc } = useTranslation();
  const next = nextActionOf(driver, t, tc);
  const stale = driver.days_in_stage >= staleDays;
  const cdl = driver.cdl_expires ? t("card.cdlExp", { date: dayjs(driver.cdl_expires).format("MMM YYYY") }) : t("card.cdlNone");
  return (
    <>
      <div className="flex items-start justify-between gap-2">
        <span className="truncate text-[13px] font-semibold">{driver.full_name}</span>
        <span
          title={t("card.daysInStage", { n: driver.days_in_stage })}
          className={cn("shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-bold", stale ? "bg-danger-soft text-danger" : "bg-fill text-ink-soft")}
        >
          {t("card.days", { n: driver.days_in_stage })}
        </span>
      </div>
      <div className="mt-0.5 text-[11px] text-muted">
        {driver.state || "—"}
        {driver.state && (
          <>
            {" "}
            <DriverLocalTime state={driver.state} />
          </>
        )}{" "}
        · {cdl}
      </div>
      {next && <div className={cn("mt-1 text-[11px]", next.tone === "danger" ? "font-semibold text-danger" : "text-ink")}>{next.text}</div>}
      <div className="mt-1 text-[11px] text-muted">{driver.owner ? <PersonLink id={driver.owner.id} name={driver.owner.display_name} /> : t("card.unassigned")}</div>
    </>
  );
});

/** DragOverlay'dagi nusxa. useDraggable chaqirmaydi: xuddi shu id bilan chaqirilsa, asl kartaning dnd-kit'dagi
 *  yozuvini bosib ketadi (o'lchash buziladi, keyingi sudrash sekinlashadi) */
export function PipelineCardPreview({ driver, staleDays }: { driver: DriverListItem; staleDays: number }) {
  return (
    <div className={cn(CARD_BASE, "rotate-1 cursor-grabbing shadow-lift ring-2 ring-accent/50")}>
      <CardContent driver={driver} staleDays={staleDays} />
    </div>
  );
}

/** Sudraladigan karta. memo: props o'zgarmasa (driver, funksiyalar barqaror) qayta chizilmaydi.
 *  O'ng tugma menyusi tashqarida: dnd-kit context'i o'zgarganda u qayta chizilmaydi */
export const PipelineCard = memo(function PipelineCard({ driver, staleDays, onOpen, menu }: PipelineCardProps) {
  const card = <DraggableCard driver={driver} staleDays={staleDays} onOpen={onOpen} />;
  return menu ? <ContextMenu items={() => menu(driver)}>{card}</ContextMenu> : card;
});

type DraggableCardProps = { driver: DriverListItem; staleDays: number; onOpen?: (driver: DriverListItem) => void } & HTMLAttributes<HTMLDivElement>;

/** dnd-kit'ga obuna bo'ladigan yagona qism (yupqa div). Radix menyusi (Trigger asChild) props va ref'ni shu yerga
 *  uzatadi: hodisa ishlovchilari dnd-kit'niki bilan birlashtiriladi (sudrash ham, sensorli ekranda uzoq bosish ham) */
const DraggableCard = forwardRef<HTMLDivElement, DraggableCardProps>(function DraggableCard({ driver, staleDays, onOpen, className, ...rest }, ref) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: `${CARD_PREFIX}${driver.id}`, data: { driver } });
  const setRef = useCallback(
    (node: HTMLDivElement | null) => {
      setNodeRef(node);
      if (typeof ref === "function") ref(node);
      else if (ref) ref.current = node;
    },
    [setNodeRef, ref],
  );
  const handlers: Record<string, unknown> = { ...rest };
  for (const [name, own] of Object.entries(listeners ?? {})) {
    const outer = handlers[name] as ((e: unknown) => void) | undefined;
    handlers[name] = outer
      ? (e: unknown) => {
          (own as (e: unknown) => void)(e);
          outer(e);
        }
      : own;
  }
  return (
    <div
      ref={setRef}
      {...handlers}
      {...attributes}
      onClick={() => onOpen?.(driver)}
      className={cn(
        CARD_BASE,
        "cursor-grab transition-[box-shadow,border-color] hover:border-accent/60 hover:shadow-lift",
        isDragging && "opacity-30",
        "data-[state=open]:border-accent data-[state=open]:ring-2 data-[state=open]:ring-accent/30",
        className,
      )}
    >
      <CardContent driver={driver} staleDays={staleDays} />
    </div>
  );
});
